import React, { useState, useEffect } from 'react';
import ProductsAPI from '../../api/products';
import Loader from '../common/Loader';

export default function CharacterProductsModal({ character, onClose }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!character?.name) return;

        const loadProducts = async () => {
            setLoading(true);
            setError('');
            try {
                const data = await ProductsAPI.getByCharacter(character.name);
                setProducts(Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : []);
            } catch (e) {
                setError(e.response?.data?.message || e.message || 'Failed to load products');
            } finally {
                setLoading(false);
            }
        };

        loadProducts();
    }, [character?.name]);

    if (!character) return null;

    return (
        <div className="fixed inset-0 z-[2147483647] flex items-center justify-center px-2 sm:px-4">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl border border-white/10 bg-gradient-to-br from-[#0a1628]/95 to-[#1a2448]/95 shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <div className="flex items-center gap-4">
                        {character.image_url && (
                            <img 
                                src={character.image_url} 
                                alt={character.display_name || character.name}
                                className="w-12 h-12 rounded-full object-cover"
                            />
                        )}
                        <div>
                            <h2 className="text-xl font-semibold">
                                {character.display_name || character.name}
                            </h2>
                            <p className="text-white/70 text-sm">Character Products</p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="text-white/70 hover:text-white transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-4 overflow-y-auto max-h-[calc(90vh-100px)]">
                    {loading ? (
                        <div className="py-16">
                            <Loader label="Loading products..." />
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <div className="text-red-300 mb-2">{error}</div>
                            <button 
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm"
                            >
                                Retry
                            </button>
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-16 text-white/60">
                            No products found for this character
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {products.map((product) => (
                                <div 
                                    key={product.id} 
                                    className="bg-white/5 rounded-lg border border-white/10 overflow-hidden hover:bg-white/10 transition-colors"
                                >
                                    {product.image_url && (
                                        <div className="aspect-square">
                                            <img 
                                                src={product.image_url} 
                                                alt={product.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    )}
                                    <div className="p-3">
                                        <h3 className="font-medium text-sm mb-1 line-clamp-2">
                                            {product.name}
                                        </h3>
                                        {product.price && (
                                            <p className="text-cyan-300 font-semibold text-lg">
                                                ${Number(product.price).toFixed(2)}
                                            </p>
                                        )}
                                        {product.description && (
                                            <p className="text-white/60 text-xs mt-2 line-clamp-2">
                                                {product.description}
                                            </p>
                                        )}
                                        {product.sku && (
                                            <p className="text-white/40 text-xs mt-1">
                                                SKU: {product.sku}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}